import React from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  IconButton,
  Grid,
  Paper,
  Divider,
} from '@mui/material';
import { ArrowBack, ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSlideDetail } from '../hooks/useAnalysis';
import { useDeckDetail } from '../hooks/useDeckDetail';
import { ChatInterface } from '../components/chat/ChatInterface';
import { LoadingOverlay } from '../components/common/LoadingOverlay';
import { ErrorDisplay } from '../components/common/ErrorDisplay';
import { FindingsList } from '../components/analysis/FindingsList';

export const SlideDetailPage: React.FC = () => {
  const { deckId, slideNumber } = useParams<{ deckId: string; slideNumber: string }>();
  const navigate = useNavigate();
  const { slide, loading: slideLoading, error: slideError, refetch } = useSlideDetail(deckId!, parseInt(slideNumber!));
  const { deck, loading: deckLoading, error: deckError } = useDeckDetail(deckId!);

  const handlePrevious = () => {
    const prevSlide = parseInt(slideNumber!) - 1;
    if (prevSlide > 0) {
      navigate(`/analysis/${deckId}/slide/${prevSlide}`);
    }
  };

  const handleNext = () => {
    const nextSlide = parseInt(slideNumber!) + 1;
    navigate(`/analysis/${deckId}/slide/${nextSlide}`);
  };

  const loading = slideLoading || deckLoading;
  const error = slideError || deckError;

  if (loading) {
    return <LoadingOverlay open={true} message="Loading slide..." />;
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <ErrorDisplay error={error} onRetry={refetch} />
      </Container>
    );
  }

  if (!slide || !deck) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography>Slide not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box mb={3}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/decks')}
            sx={{ cursor: 'pointer' }}
          >
            My Presentations
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/analysis/${deckId}`)}
            sx={{ cursor: 'pointer' }}
          >
            {deck.fileName}
          </Link>
          <Typography color="text.primary">Slide {slideNumber}</Typography>
        </Breadcrumbs>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate(`/analysis/${deckId}`)}
            >
              Back to Analysis
            </Button>
          </Box>

          <Box display="flex" gap={1} alignItems="center">
            <IconButton
              onClick={handlePrevious}
              disabled={parseInt(slideNumber!) <= 1}
              size="small"
            >
              <ArrowBackIos />
            </IconButton>
            <Typography variant="body1" sx={{ px: 2 }}>
              Slide {slideNumber}
            </Typography>
            <IconButton onClick={handleNext} size="small">
              <ArrowForwardIos />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ height: '600px' }}>
        {/* Left Pane - Slide Content */}
        <Grid item xs={12} lg={6} sx={{ height: '100%' }}>
          <Paper sx={{ p: 3, height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h5" gutterBottom>
              Slide {slide.slideNumber}
            </Typography>

            {slide.slideTitle && (
              <Typography 
                variant="h6" 
                color="text.secondary" 
                gutterBottom
                title={slide.slideTitle}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {slide.slideTitle}
              </Typography>
            )}

            {slide.imageUrl && (
              <Box sx={{ mb: 3, mt: 2 }}>
                <Box
                  component="img"
                  src={slide.imageUrl}
                  alt={`Slide ${slide.slideNumber}`}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 2,
                  }}
                />
              </Box>
            )}

            {slide.extractedText && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Extracted Text
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    bgcolor: 'background.default',
                    p: 2,
                    borderRadius: 1,
                  }}
                >
                  {slide.extractedText}
                </Typography>
              </>
            )}

            {/* Slide Issues/Findings */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Slide Issues
            </Typography>
            <FindingsList
              findings={slide.findings || []}
              deckId={deckId!}
              slideNumber={slide.slideNumber}
              onAddressedChange={refetch}
            />
          </Paper>
        </Grid>

        {/* Right Pane - Chat Interface */}
        <Grid item xs={12} lg={6} sx={{ height: '100%' }}>
          <ChatInterface deckId={deckId!} slideNumber={parseInt(slideNumber!)} />
        </Grid>
      </Grid>
    </Container>
  );
};

